import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { api } from "../../services/api";

function FollowUpMonitor() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get("/analytics/followup-service/status");
      setStatus(response.data.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching follow-up service status:", err);
      setError("Errore nel recuperare lo stato del servizio di follow-up");
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    try {
      setActionLoading(true);
      await api.post("/analytics/followup-service/start");
      await fetchStatus(); // Refresh status
    } catch (err) {
      console.error("Error starting follow-up service:", err);
      setError("Errore nell'avviare il servizio di follow-up");
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    try {
      setActionLoading(true);
      await api.post("/analytics/followup-service/stop");
      await fetchStatus(); // Refresh status
    } catch (err) {
      console.error("Error stopping follow-up service:", err);
      setError("Errore nel fermare il servizio di follow-up");
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Refresh status every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !status) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h2">
            🔄 Servizio Follow-up Automatico
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Aggiorna stato">
              <IconButton onClick={fetchStatus} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {status && (
              <>
                {!status.running ? (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<StartIcon />}
                    onClick={handleStart}
                    disabled={actionLoading || !status.enabled}
                    size="small"
                  >
                    Avvia
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<StopIcon />}
                    onClick={handleStop}
                    disabled={actionLoading}
                    size="small"
                  >
                    Ferma
                  </Button>
                )}
              </>
            )}
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {status && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Stato Configurazione
                </Typography>
                <Chip
                  icon={status.enabled ? <ActiveIcon /> : <InactiveIcon />}
                  label={status.enabled ? "Abilitato" : "Disabilitato"}
                  color={status.enabled ? "success" : "default"}
                  variant="outlined"
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Stato Servizio
                </Typography>
                <Chip
                  icon={status.running ? <ActiveIcon /> : <InactiveIcon />}
                  label={status.running ? "In Esecuzione" : "Fermato"}
                  color={status.running ? "success" : "error"}
                  variant="outlined"
                />
              </Box>
            </Grid>

                        <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Timeout Inattività
                </Typography>
                <Typography variant="h6" color="primary">
                  {status.inactivity_minutes} min
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Delay Minimo
                </Typography>
                <Typography variant="h6" color="primary">
                  {status.min_delay_hours || 24} ore
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Box textAlign="center">
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Utenti Tracciati
                </Typography>
                <Typography variant="h6" color="primary">
                  {status.active_users_tracked || 0}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Box textAlign="center">
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Conversazioni Processate
                </Typography>
                <Typography variant="h6" color="primary">
                  {status.processed_conversations || 0}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box textAlign="center">
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Ultimo Aggiornamento
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {new Date().toLocaleTimeString()}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  <InfoIcon fontSize="small" sx={{ mr: 1, verticalAlign: "middle" }} />
                  Messaggio di Follow-up
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    backgroundColor: "grey.100", 
                    p: 1, 
                    borderRadius: 1,
                    fontStyle: "italic"
                  }}
                >
                  {status.message_text}
                </Typography>
              </Box>
            </Grid>

            {!status.enabled && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Il servizio di follow-up è disabilitato nella configurazione. 
                  Controllare la variabile FOLLOWUP_ENABLED nel file .env
                </Alert>
              </Grid>
            )}

            {status.enabled && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    🛡️ Protezioni Anti-Ricorsive Attive:
                  </Typography>
                  <Typography variant="body2" component="div">
                    • <strong>Delay minimo:</strong> {status.min_delay_hours || 24} ore tra follow-up per la stessa conversazione<br/>
                    • <strong>Controllo contenuto:</strong> Rileva follow-up già inviati nei messaggi recenti<br/>
                    • <strong>Controllo AI model:</strong> Identifica messaggi dal sistema "followup-system"<br/>
                    • <strong>Controllo timing:</strong> Previene follow-up troppo ravvicinati alle risposte AI<br/>
                    • <strong>Isolamento conversazioni:</strong> Ogni conversazione è tracciata indipendentemente
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}

export default FollowUpMonitor;
